import { useRef, useCallback } from "react";
import Editor, { OnMount, OnChange } from "@monaco-editor/react";
import type { editor } from "monaco-editor";

interface HtmlCodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  height?: string | number;
  readOnly?: boolean;
  theme?: "vs-dark" | "light";
  className?: string;
}

/**
 * Monaco-based HTML code editor with syntax highlighting
 * Provides VS Code-like editing experience for HTML templates
 */
export default function HtmlCodeEditor({
  value,
  onChange,
  height = "400px",
  readOnly = false,
  theme = "vs-dark",
  className = "",
}: HtmlCodeEditorProps) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleEditorMount: OnMount = useCallback((editor) => {
    editorRef.current = editor;
    
    // Configure editor options after mount
    editor.updateOptions({
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: "on",
      lineNumbers: "on",
      renderLineHighlight: "all",
      folding: true,
      automaticLayout: true,
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: true,
      bracketPairColorization: { enabled: true },
    });
  }, []);

  const handleChange: OnChange = useCallback(
    (newValue) => {
      if (onChange && newValue !== undefined) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  return (
    <div className={`rounded-md border overflow-hidden ${className}`}>
      <Editor
        height={height}
        defaultLanguage="html"
        value={value}
        onChange={handleChange}
        onMount={handleEditorMount}
        theme={theme}
        options={{
          readOnly,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: "on",
          lineNumbers: "on",
          renderLineHighlight: "all",
          folding: true,
          automaticLayout: true,
          tabSize: 2,
          insertSpaces: true,
          formatOnPaste: true,
          bracketPairColorization: { enabled: true },
          fontSize: 13,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
          padding: { top: 12, bottom: 12 },
        }}
        loading={
          <div className="flex items-center justify-center h-full bg-gray-900 text-gray-400">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Loading editor...</span>
            </div>
          </div>
        }
      />
    </div>
  );
}

